<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
  <section class="cart-module">
    <div class="cart-inner-container">
      <FormKit
        type="form"
        id="formKit"
        #default="{ value, state: { valid } }"
        :plugins="[stepPlugin]"
        @submit="submitHandler"
        @submit-invalid="showErrors"
        :actions="false"
      >
        <!-- content -->
        <!-- cart -->
        <div v-show="cart.length > 0 || finishes.length > 0">
          <!-- sticky stepper nav -->
          <div class="steps-container" ref="stepsContainer">
            <ul class="steps">
              <li
                v-for="(section, sIndex) in sectionLabels"
                :key="`section-${sIndex}`"
                :class="['step', { 'is-active': currentSection === sIndex }]"
                @click="scrollToSection(sIndex)"
                :data-step-valid="true"
                :data-step-active="currentSection === sIndex"
              >
                <span
                  class="step-number"
                  v-text="`0${sIndex + 1}`"
                />
                {{ section }}
              </li>
            </ul>
            <div class="next">
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="formkit-input"
                  :disabled="!valid || disabled"
                  @click="handleSubmitClick"
                >{{ labelSend }}</button>
              </div>
            </div>
          </div>
          <div class="main-container cart-container">
            <!-- Section 1: Products -->
            <div id="section-products" ref="sectionProducts">
              <h2 class="text-3xl lg:text-[60px] mt-20 mb-10 text-ore_green font-medium">Products</h2>
              <CartCard  v-for="(item, index) of cart" :key="`${index}-cart-card`" :data="item" :index="index" />
              <div v-if="cart.length === 0">
                <p class="mb-6">No products added to your quote...</p>
                <Button
                  color="green"
                  lineHover="bg-green"
                  circleColor="circle-white"
                  :url="data.products_page.url"
                  :text="data.products_page.title"
                />
              </div>
            </div>
            <!-- Finish Samples (grouped with Products) -->
            <div class="section-group">
              <h3 class="section-heading">Finish Samples</h3>
              <div class="flex gap-5 flex-wrap">
                <CartFinishCard v-for="(finish, index) of finishes" :key="`${index}-cart-card`" :finish="finish.finish" :category="finish.finishId"  />
              </div>
              <div v-if="finishes.length === 0">
                <p class="mb-6">No finish samples added to your quote...</p>
                <Button
                  color="green"
                  lineHover="bg-green"
                  circleColor="circle-white"
                  :url="data.finish_samples_page.url"
                  :text="data.finish_samples_page.title"
                />
              </div>
            </div>
            <!-- Section 2 & 3: Form fields (Contact & Project sections rendered via schema) -->
            <div class="form-body" v-if="isDataFormLoaded">
              <div class="step-content-container">
                <div
                  class="step-content"
                  v-for="(step, index) in props.data.steps_names"
                  :key="`step-content-${index}`"
                  >
                  <ul class="validation-errors" v-if="messages.length">
                    <li v-for="(message, indexMessage) in messages" :key="`message-${indexMessage}`">
                      {{ message }}
                    </li>
                  </ul>
                  <FormKit
                    :id="step.step_name"
                    type="group"
                    :name="step.step_name"
                    v-model="requestFormData"
                  >
                    <FormKitSchema :schema="formByPage[index]" :data="requestFormData"/>
                  </FormKit>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-show="cart.length === 0 && finishes.length === 0">
          <div class="empty-cart-container">

            <h1>{{data.empty_cart_message}}</h1>
            <div class="links">
              <a class='nav-links' :href="link.link.url" :target="link.link.target" v-for="(link, index) of data.empty_cart_links" :key="`${index}-link`">{{link.link.title}}</a>
            </div>
          </div>
          <div class="carousel-container">
            <h2 class="related-products-title" v-html="data.empty_cart_featured_products_title" />
          </div>
          <div class="carousel-container">
            <div v-for="(product, index) of data.empty_cart_products" :key="index" class="carousel-card" :class="index > 2 ? 'block md:hidden' : ''">
              <a @click="router.push(`/${product.post_type}/${product.post_title.replaceAll(' ', '-').toLowerCase()}`)" class="carousel-card-link cursor-pointer">
                <img :src="product.acf['acf-single-product-image'].sizes.large" alt="" class="product-carousel-image" />
                <p>{{product.post_title}}</p>
              </a>
            </div>
          </div>
        </div>
      </FormKit>
    </div>
  </section>
</template>
<script setup>
import { onBeforeMount, onMounted, onBeforeUnmount, ref } from 'vue'
import Button from '@/components/utility/Button.vue'
import { useStore } from 'vuex'
import { cartState } from './Store.js'
import { finishState } from './finishStore.js'
import { useRouter } from 'vue-router'
import { reset, getNode } from '@formkit/core'
import { getValidationMessages } from '@formkit/validation'
import Axios from 'axios'
import camel2title from '../../../utils/utils'
import useSteps from '../../../utils/useSteps'
import CartCard from './CartCard.vue'
import CartFinishCard from './CartFinishCard.vue'
import { useDecimalToInches } from '@/composables/useDecimalToInches.js'

const props = defineProps(['data'])
const { steps, visitedSteps, activeStep, setStep, stepPlugin } = useSteps()
const store = useStore()
const router = useRouter()
const form = ref({})
const formByPage = ref({})
const storeParams = ref({})
const requestFormData = ref({})
const messages = ref([])
const activeIndex = ref(1)
const isDataFormLoaded = ref(false)
const disabled = ref(false)
const labelSend = ref('Submit')
const quoteForm = store._state.data.quote_form
const { cart, clearCart } = cartState()
const { finishes, clearFinishes } = finishState()

// Section navigation
const sectionLabels = ref(['Products & Samples', 'Contact Information', 'Project Information'])
const currentSection = ref(0)
const sectionProducts = ref(null)
const stepsContainer = ref(null)

const getSectionElements = () => {
  return [
    sectionProducts.value,
    document.getElementById('section-contact-fields'),
    document.getElementById('section-project-fields')
  ]
}

const scrollToSection = (index) => {
  const sections = getSectionElements()
  const el = sections[index]
  if (el) {
    const navHeight = stepsContainer.value?.offsetHeight || 80
    const headerOffset = 70
    const top = el.getBoundingClientRect().top + window.scrollY - navHeight - headerOffset - 10
    window.scrollTo({ top, behavior: 'smooth' })
  }
}

const updateCurrentSection = () => {
  const sections = getSectionElements()
  const navHeight = (stepsContainer.value?.offsetHeight || 80) + 70 + 40
  for (let i = sections.length - 1; i >= 0; i--) {
    if (sections[i]) {
      const rect = sections[i].getBoundingClientRect()
      if (rect.top <= navHeight) {
        currentSection.value = i
        return
      }
    }
  }
  currentSection.value = 0
}

let scrollHandler = null

onMounted(() => {
  scrollHandler = () => updateCurrentSection()
  window.addEventListener('scroll', scrollHandler, { passive: true })
})

onBeforeUnmount(() => {
  if (scrollHandler) {
    window.removeEventListener('scroll', scrollHandler)
  }
})

const showErrors = (node) => {
  messages.value = []
  const validations = getValidationMessages(node)
  validations.forEach((inputMessages) => {
    messages.value = messages.value.concat(
      inputMessages.map((message) => message.value)
    )
  })
  console.log(messages.value)
}

const handleSubmitClick = async () => {
  if (disabled.value) return
  try {
    await submitHandler(requestFormData.value, getNode('formKit'))
  } catch (e) {
    console.error('Submit error:', e)
    disabled.value = false
    labelSend.value = 'Submit'
  }
}

const returnBack = () => {
  if (props.data.return_link.url) {
    const url = props.data.return_link.url.replace(store.state.site.url, '')
    router.push(url)
  } else {
    router.push('/')
  }
}
const postData = async (URL, body, node) => {
  try {
    const response = await Axios.post(URL, body, {
      headers: {
        'Content-Type': 'multipart/form-data',
        withCredentials: true
      }
    })
    disabled.value = false
    labelSend.value = 'Submit'
    messages.value = []
    if (response.status === 200 && response.data.is_valid) {
      if (node) node.clearErrors()
      reset('formKit')
      const path = props.data.thank_you_page_url.url.replace(store._state.data.site.url, '')
      router.push(path)
    }
  } catch (error) {
    disabled.value = false
    labelSend.value = 'Submit'
    if (node) node.setErrors(error.formErrors, error.fieldErrors)
    console.error(`There was an error submitting the form: ${error}`)
  }
}

const navigateThankYou = async () => {
  await router.push(props.data.thank_you_page_url.url.replace(store._state.data.site.url, ''))
  clearCart()
  clearFinishes()
}

// NEW POSTING METHOD
// Add Quote Products to Gravity forms
const addToGFQuote = (cart) => {
  const data = cart.value.map((x) => {
    // console.log(x)
    return {
      image: x.currentImage,
      product: x.product.title.rendered,
      finish: `${x.finishCategory} - ${x.finish['acf-single-product-options-finish-option'].label}`,
      size: x.size.attributes.attribute_size,
      options: x.designOptions.map((option) => ` ${option.label}`),
      length: useDecimalToInches(x.size.dimensions.length).toRegularString(),
      width: useDecimalToInches(x.size.dimensions.width).toRegularString(),
      height: useDecimalToInches(x.size.dimensions.height).toRegularString(),
      diameter: useDecimalToInches(x.size?.diameter).toRegularString() || '',
      quantity: x.quantity
    }
  })
  return data
}

const addFinishToGFQuote = (cart) => {
  const data = finishes.value.map((x) => {
    return {
      image: x.finish['acf-product-options-finish-image'].url,
      finish: x.finishId
    }
  })
  return data
}

const submitHandler = async (_data, node) => {
  disabled.value = true
  labelSend.value = 'Sending...'
  messages.value = []
  const curedForm = new FormData()
  const requestFormDataHelper = { ...requestFormData.value }
  if ('slots' in requestFormDataHelper) {
    delete requestFormDataHelper.slots
  }
  const keys = Object.keys(requestFormDataHelper)
  keys.forEach(element => {
    if (element === 'Finish request') {
      const formElement = form.value.fields.find((x) => x.label === 'Finish request')
      addFinishToGFQuote(cart).forEach((x) => {
        for (const prop in x) {
          curedForm.append(`input_${formElement.id}[]`, x[prop])
        }
      })
      return
    }
    if (element === 'Product list') {
      // console.log(form.value)
      const formElement = form.value.fields.find((x) => x.label === 'Product list')
      addToGFQuote(cart).forEach((x) => {
        for (const prop in x) {
          curedForm.append(`input_${formElement.id}[]`, x[prop])
        }
      })
      return
    }
    if (requestFormDataHelper[element] === undefined) {
      return
    }
    const fieldSettings = form.value.fields.find((x) => {
      const result = x.type === 'checkbox' ? element.replaceAll('_', ' ') : element
      return x.label === result
    })
    if (fieldSettings.type === 'address' || fieldSettings.type === 'name') {
      requestFormDataHelper[element].forEach((subElement, indexSubElement) => {
        curedForm.append(`input_${fieldSettings.inputs[indexSubElement].id.replace('.', '_')}`, subElement ?? '')
      })
    } else if (fieldSettings.type === 'checkbox') {
      curedForm.append(`input_${fieldSettings.id}_1`, requestFormDataHelper[element] ? fieldSettings.label : '')
    } else if (fieldSettings.type === 'date') {
      let result = ''
      if (requestFormDataHelper[element]) {
        const date = requestFormDataHelper[element]
        const [year, month, day] = date.split('-')
        result = [month, day, year].join('/')
      }
      curedForm.append(`input_${fieldSettings.id}`, result)
    } else {
      curedForm.append(`input_${fieldSettings.id}`, fieldSettings.type === 'fileupload' ? requestFormDataHelper[element].map(a => a.file)[0] ?? '' : requestFormDataHelper[element] ?? '')
    }
  })

  // await addToYITHQuote(cart)
  await postData(form.value.submitURL, curedForm, node)
  // await Axios.get(`${store._state.data.site.url}/wp-json/wp/v2/clear-quote`)
  navigateThankYou()
}

const checkStepValidity = (stepName) => {
  console.log(stepName)
  return (steps[stepName].errorCount > 0 || steps[stepName].blockingCount > 0) && visitedSteps.value.includes(stepName)
}

const selectOptions = (choices) => {
  if (choices) {
    return choices.map((choice) => {
      return choice.text
    })
  } else {
    return []
  }
}

const validationTypes = (required, type) => {
  const validation = []
  if (required) {
    validation.push('required')
  }
  if (type === 'email') {
    validation.push('email')
  }
  if (type === 'phone') {
    validation.push('number')
    validation.push('length:10,10')
  }
  if (type === 'zipcode') {
    validation.push('number')
    validation.push('length:5,5')
  }
  const finalValidation = validation.join('|')
  return finalValidation
}

const returnFormKiType = (type) => {
  switch (type) {
    case 'phone':
      return 'tel'
    case 'fileupload':
      return 'file'
    case 'address':
    case 'name':
    case 'checkbox':
      return 'list'
    default:
      return type
  }
}

const buildFieldSettings = (field) => {
  const validations = validationTypes(field.isRequired, field.type)
  let fieldSettings = {
    $formkit: returnFormKiType(field.type),
    name: field.label,
    label: field.labelPlacement ? '' : field.label,
    placeholder: field.placeholder,
    validation: validations,
    outerClass: field.cssClass,
    validationVisibility: 'blur'
  }

  if (field.conditionalLogic && field.conditionalLogic?.rules.length > 0) {
    fieldSettings.if = `$${field.conditionalLogic.rules[0].value.replaceAll(' ', '_')} !== true`
  }

  if (field.type === 'select') {
    fieldSettings.options = selectOptions(field.choices)
  }
  if (field.type === 'textarea') {
    fieldSettings.rows = 4
  }
  if (field.type === 'fileupload') {
    fieldSettings.accept = field.allowedExtensions ?? ''
    fieldSettings.multiple = false
  }
  if (field.type === 'address' || field.type === 'name') {
    const children = []
    if (field.labelPlacement.length === 0) {
      children.push({
        $el: 'div',
        attrs: { class: 'formkit-outer always-full' },
        children: [{
          $el: 'div',
          attrs: { class: 'formkit-wrapper' },
          children: [{
            $el: 'label',
            attrs: { class: 'formkit-label' },
            children: field.label || field.placeholder
          }]
        }]
      })
    }
    field.inputs.forEach(element => {
      children.push({
        $formkit: element.isHidden ? 'hidden' : 'text',
        name: element.label,
        label: field.subLabelPlacement === 'hidden_label' ? '' : element.customLabel !== '' ? element.customLabel : element.label,
        placeholder: element.placeholder,
        validation: element.isHidden ? [] : element.label === 'ZIP / Postal Code' ? validationTypes(field.isRequired, 'zipcode') : validations,
        validationLabel: element.placeholder !== '' ? element.placeholder : element.customLabel !== '' ? element.customLabel : element.label,
        outerClass: 'half-width',
        autoComplete: element.placeholder
      })
    })
    fieldSettings.children = children
  }

  if (field.type === 'checkbox') {
    fieldSettings = {}
    fieldSettings.$formkit = 'checkbox'
    fieldSettings.name = (field.choices[0].value).replaceAll(' ', '_')
    fieldSettings.label = field.choices[0].text
    fieldSettings.value = true
  }

  return fieldSettings
}

const isContactField = (field) => {
  const contactTypes = ['name', 'email', 'phone', 'address']
  if (contactTypes.includes(field.type)) return true
  if (field.label && field.label.toLowerCase().includes('company')) return true
  if (field.type === 'checkbox') return true
  return false
}

const setFormByPage = () => {
  const schema = [[{
    $formkit: 'hidden',
    name: 'hidden',
    type: 'hidden'
  }]]

  const personalFields = []   // name, email, phone, company
  const shippingFields = []   // shipping address
  const addressToggle = []    // "same as" checkbox
  const billingFields = []    // billing address (conditional)
  const projectFields = []    // everything else

  form.value.fields.forEach((field) => {
    if (field.type === 'page') return

    const fieldSettings = buildFieldSettings(field)

    // Override display label for "Street Address" → "Billing Address"
    if (field.type === 'address' && field.label === 'Street Address') {
      // Find and update the label div in children (first child is the label wrapper)
      if (fieldSettings.children && fieldSettings.children.length > 0) {
        const labelWrapper = fieldSettings.children[0]
        if (labelWrapper.$el === 'div' && labelWrapper.children) {
          const innerWrapper = labelWrapper.children[0]
          if (innerWrapper && innerWrapper.children) {
            const labelEl = innerWrapper.children[0]
            if (labelEl && labelEl.$el === 'label') {
              labelEl.children = 'Billing Address'
            }
          }
        }
      }
    }

    // Override checkbox display label
    if (field.type === 'checkbox' && field.label.toLowerCase().includes('same as street')) {
      fieldSettings.label = 'Shipping Address is the same as Billing Address'
    }

    // Categorize into groups for correct ordering
    if (field.type === 'address' && field.label === 'Shipping Address') {
      shippingFields.push(fieldSettings)
    } else if (field.type === 'address' && field.label === 'Street Address') {
      billingFields.push(fieldSettings)
    } else if (field.type === 'checkbox' && field.label.toLowerCase().includes('same as')) {
      addressToggle.push(fieldSettings)
    } else if (isContactField(field)) {
      personalFields.push(fieldSettings)
    } else {
      projectFields.push(fieldSettings)
    }
  })

  // Order: personal info → shipping address → checkbox → billing address
  const allContactFields = [...personalFields, ...shippingFields, ...addressToggle, ...billingFields]

  schema[1] = [
    {
      $el: 'div',
      attrs: { id: 'section-contact-fields', class: 'section-group' },
      children: [
        { $el: 'h3', attrs: { class: 'section-heading' }, children: 'Contact & Shipping' },
        { $el: 'div', attrs: { class: 'section-fields' }, children: allContactFields }
      ]
    }
  ]

  schema[2] = [
    {
      $el: 'div',
      attrs: { id: 'section-project-fields', class: 'section-group' },
      children: [
        { $el: 'h3', attrs: { class: 'section-heading' }, children: 'Project Details' },
        { $el: 'div', attrs: { class: 'section-fields' }, children: projectFields }
      ]
    }
  ]

  return schema
}

const getFormData = async () => {
  storeParams.value = {
    type: 'forms',
    id: quoteForm.gf_id,
    showLoading: true
  }
  await store.dispatch('getSingleById', storeParams.value)
  form.value = store.getters.singleById(storeParams.value)
  // console.log(form.value)
  formByPage.value = setFormByPage()
  isDataFormLoaded.value = true
}

const refreshScrollTrigger = () => {
  setTimeout(() => {
    // ScrollTrigger.refresh()
  }, 0)
}

onBeforeMount(() => {
  getFormData()
  refreshScrollTrigger()
})

</script>
<style lang="scss" scoped>
.cart-module {
  @apply wide-container bg-ore_mint_30 pt-48 pb-60 lg:px-14;
  .steps-container {
    @apply flex flex-col lg:flex-row items-center justify-between gap-y-2 bg-ore_green text-ore_mint_30 font-FHOscar font-normal text-2xl leading-6 px-[22px] lg:px-7 py-[15px] lg:py-5 rounded-[20px] mb-10;
    position: sticky;
    top: 70px;
    z-index: 9;

    .steps {
      @apply w-full flex flex-col lg:flex-row items-start justify-center lg:items-center;

      .step {
        @apply flex flex-col text-2xl leading-6 font-FHOscar font-normal text-ore_mint_30 lg:border-r lg:border-ore_orange lg:px-[61px] cursor-pointer opacity-60 transition-opacity duration-300;
        .step-number {
          @apply hidden lg:flex items-center lg:relative font-FHOscar font-normal text-xl leading-6 text-ore_mint;

          &::after {
            @apply bg-ore_orange w-[10px] h-[10px] rounded-[10px] absolute left-8;
            content: "";
          }
        }

        &:hover {
          @apply opacity-80;
        }
      }

      .step.is-active {
        @apply opacity-100;
      }

      .step:last-child {
        @apply border-none;
      }
    }
    .next:deep(){
      @apply flex justify-end items-center flex-shrink-0;
      .formkit-input {
        @apply bg-ore_orange text-white rounded-3xl text-[20px] leading-6 py-2 px-10 transition-all duration-500;
        filter: drop-shadow(0px 0px 30px rgba(255, 76, 0, 0.5));
      }
      .formkit-input[disabled]{
        @apply opacity-60;
      }
      &:hover{
        @apply scale-[1.025];
      }
    }
  }
  .cart-container {
    .form-body:deep() {
      @apply flex w-full;

      .section-group {
        @apply w-full bg-white rounded-3xl p-6 lg:p-10 mb-8;
      }
      .section-heading {
        @apply font-FHOscar font-normal text-ore_green text-xl lg:text-2xl leading-7 pb-3 mb-4 border-b border-ore_green/40;
      }
      .section-fields {
        @apply flex flex-col md:flex-row md:flex-wrap w-full;

        .formkit-outer{
          @apply flex flex-col w-full lg:basis-2/4 mb-2;
          &.half-width{
            @apply basis-2/4;
          }
          &:nth-of-type(even){
            @apply md:basis-[48%] mr-[2%];
          }
          &:nth-of-type(odd), &.always-full ~ .half-width:nth-of-type(even){
            @apply lg:basis-[48%] lg:mr-[2%];
          }

          &.always-full {
            @apply md:basis-full lg:basis-full;
            .formkit-wrapper{
              @apply lg:w-[48%];
              .formkit-label {
                @apply w-1/2 lg:w-full;
              }
            }
          }
          .formkit-wrapper {
            @apply w-full flex flex-col;
            .formkit-label{
              @apply text-ore_green font-FHOscar font-normal text-base leading-7 mt-4 lg:mt-5 py-1 ml-4 lg:ml-0 border-t-0 w-auto;
            }
            .formkit-inner{
              @apply flex flex-col w-full;
                &:focus-visible, &:focus {
                --tw-ring-shadow: none;
                @apply border-none outline-none shadow-none;
              }
              .formkit-input {
                @apply flex w-full border-none text-ore_green p-3 rounded-xl font-FHOscar font-normal text-base leading-7;
                background-color: #f0f5f1;
                &:focus-visible, &:focus {
                  --tw-ring-shadow: none;
                  @apply border-none outline-none shadow-none;
                }
                &[type="date"]{
                  @apply cursor-pointer;
                }
                &[type="file"]{
                  @apply text-transparent flex items-center justify-center pl-2 leading-none;
                  &::-webkit-file-upload-button {
                    @apply invisible;
                  }
                  &::before {
                    content: "Attach File";
                    -webkit-user-select: none;
                    @apply flex items-center justify-center text-white bg-ore_orange rounded-[100px] outline-none whitespace-nowrap cursor-pointer text-base lg:text-lg py-1 px-6 lg:px-8 w-36 lg:w-44;
                  }
                }
              }
              select {
                @apply cursor-pointer;
              }
              .formkit-no-files{
                @apply pl-2;
              }
              .formkit-file-list{
                @apply flex flex-col lg:flex-row lg:flex-wrap gap-y-1 lg:gap-x-1 pl-2 lg:pl-0;
                .formkit-file-item{
                  @apply flex flex-row-reverse items-center justify-center bg-ore_green text-white rounded-[100px] text-lg lg:text-xl leading-10 py-1 lg:py-4 px-6 lg:px-20 gap-x-8 relative;
                  .formkit-file-name {
                    @apply inline-block text-ellipsis max-w-full overflow-hidden whitespace-nowrap;
                  }
                }
                .formkit-file-remove{
                  @apply text-transparent py-0 w-10 h-10 absolute left-0 top-[6px] lg:top-3;
                  &::before{
                    content: url('@/assets/img/remove.svg');
                  }
                }
              }
              .formkit-file-remove{
                @apply ml-2 text-xl text-ore_green_dark py-4;
              }
            }
          }
          .formkit-messages{
            @apply text-red-500 text-base pl-3 pt-1;
          }
          .formkit-help{
            @apply text-ore_green text-[14px] pl-3 pt-1;
          }
        }
        .formkit-outer[data-type="checkbox"]{
          @apply md:basis-full;
          .formkit-wrapper {
            @apply flex-row justify-start items-center mt-4;
            .formkit-inner {
              @apply max-w-max;
              .formkit-input{
                @apply max-w-max cursor-pointer;
              }
              input.formkit-input{
                @apply rounded-[100px] bg-cover bg-transparent lg:mr-4;
                background-image: url('@/assets/img/checkbox-unchecked.svg');
              }
              input.formkit-input:checked{
                @apply bg-auto;
                background-image: url('@/assets/img/checkbox-checked.svg');
              }
            }
            .formkit-label{
              @apply my-0 border-t-0 py-0 leading-6 cursor-pointer;
            }
          }
        }
        .formkit-outer[data-type="select"]{
          .formkit-wrapper{
            .formkit-inner{
              select.formkit-input {
                @apply bg-auto;
                background-image: url('@/assets/img/down-circle-arrow-orange.svg');
              }
            }
          }
        }
      }

      .step-content-container {
        @apply w-full flex flex-col py-4 md:py-12 desktop:py-14;
        .step-content {
          @apply flex flex-col md:flex-row md:flex-wrap w-full;
          .validation-errors {
            @apply bg-red-200 text-red-500 rounded-2xl p-4 mt-2;
          }
          .formkit-outer{
            @apply flex flex-col w-full lg:basis-2/4 mb-2;
            &.half-width{
              @apply basis-2/4;
            }
            &:nth-of-type(even){
              @apply md:basis-[48%] mr-[2%];
            }
            &:nth-of-type(odd), &.always-full ~ .half-width:nth-of-type(even){
              @apply lg:basis-[48%] lg:mr-[2%];
            }

            &.always-full {
              @apply md:basis-full lg:basis-full;
              .formkit-wrapper{
                @apply lg:w-[48%];
                .formkit-label {
                  @apply w-1/2 lg:w-full;
                }
              }
            }
            .formkit-wrapper {
              @apply w-full flex flex-col;
              .formkit-label{
                @apply text-ore_green font-FHOscar font-normal text-base leading-7 mt-4 lg:mt-5 py-1 ml-4 lg:ml-0 border-t-0 w-auto;
              }
              .formkit-inner{
                @apply flex flex-col w-full;
                  &:focus-visible, &:focus {
                  --tw-ring-shadow: none;
                  @apply border-none outline-none shadow-none;
                }

                .formkit-input {
                  @apply flex w-full border-none text-ore_green p-3 rounded-xl font-FHOscar font-normal text-base leading-7;

                  &:focus-visible, &:focus {
                    --tw-ring-shadow: none;
                    @apply border-none outline-none shadow-none;
                  }

                  &[type="date"]{
                    @apply cursor-pointer;
                  }

                  &[type="file"]{
                    @apply text-transparent flex items-center justify-center pl-2 leading-none;

                    &::-webkit-file-upload-button {
                      @apply invisible;
                    }

                    &::before {
                      content: "Attach File";
                      -webkit-user-select: none;
                      @apply flex items-center justify-center text-white bg-ore_orange rounded-[100px] outline-none whitespace-nowrap cursor-pointer text-base lg:text-lg py-1 px-6 lg:px-8 w-36 lg:w-44;
                    }
                  }
                }

                select {
                  @apply cursor-pointer;
                }

                .formkit-no-files{
                  @apply pl-2;
                }

                .formkit-file-list{
                  @apply flex flex-col lg:flex-row lg:flex-wrap gap-y-1 lg:gap-x-1 pl-2 lg:pl-0;

                  .formkit-file-item{
                    @apply flex flex-row-reverse items-center justify-center bg-ore_green text-white rounded-[100px] text-lg lg:text-xl leading-10 py-1 lg:py-4 px-6 lg:px-20 gap-x-8 relative;
                    .formkit-file-name {
                      @apply inline-block text-ellipsis max-w-full overflow-hidden whitespace-nowrap;
                    }
                  }
                  .formkit-file-remove{
                    @apply text-transparent py-0 w-10 h-10 absolute left-0 top-[6px] lg:top-3;
                    &::before{
                      content: url('@/assets/img/remove.svg');
                    }
                  }
                }

                .formkit-file-remove{
                  @apply ml-2 text-xl text-ore_green_dark py-4;
                }
              }
            }
            .formkit-messages{
              @apply text-red-500 text-base pl-3 pt-1;
            }

            .formkit-help{
              @apply text-ore_green text-[14px] pl-3 pt-1;
            }
          }
          .formkit-input[type="hidden"] ~ .formkit-outer:nth-of-type(odd):not([data-type="checkbox"]):not(.always-full){
            @apply lg:basis-2/4 lg:mr-0;
          }
          .formkit-outer[data-type="checkbox"] ~ .formkit-outer:nth-of-type(even){
            @apply md:basis-full md:mr-0 lg:basis-[48%] lg:mr-[2%];
          }
          .formkit-outer[data-type="checkbox"]{
            @apply md:basis-full;
            .formkit-wrapper {
              @apply flex-row justify-start items-center mt-4;
              .formkit-inner {
                @apply max-w-max;
                .formkit-input{
                  @apply max-w-max cursor-pointer;
                }
                input.formkit-input{
                  @apply rounded-[100px] bg-cover bg-transparent lg:mr-4;
                  background-image: url('@/assets/img/checkbox-unchecked.svg');
                }
                input.formkit-input:checked{
                  @apply bg-auto;
                  background-image: url('@/assets/img/checkbox-checked.svg');
                }
              }
              .formkit-label{
                @apply my-0 border-t-0 py-0 leading-6 cursor-pointer;
              }
            }
          }
          .formkit-outer[data-type="select"]{
            .formkit-wrapper{
              .formkit-inner{
                select.formkit-input {
                  @apply bg-auto;
                  background-image: url('@/assets/img/down-circle-arrow-orange.svg');
                }
              }
            }
          }
        }
      }
    }
  }
  .empty-cart-container {
    @apply h-[600px] rounded-3xl p-10 md:p-20 bg-white flex flex-col justify-center relative;
    h1 {
      @apply text-3xl leading-tight lg:text-[120px] text-ore_green mb-32;
    }

    .links {
      @apply flex flex-col md:flex-row;
      .nav-links {
        @apply text-[24px] text-ore_green relative mr-16 w-max underline;
        &:not(:first-child) {
          @apply ml-0 md:ml-12;
        }
        &::before {
          @apply content-[''] absolute border border-solid border-ore_green rounded-full h-2 w-2 -right-8 top-4 pointer-events-none;
        }
        &:not(:last-child)::after {
          @apply content-none md:content-[''] absolute border-r border-solid border-ore_orange h-16 w-1 -right-20 -top-3 pointer-events-none;
        }
      }
    }
  }

  .carousel-container {
      @apply flex flex-row py-5 gap-5 flex-wrap md:flex-nowrap;
      .related-products-title {
        @apply text-ore_orange text-[40px] my-10;
      }
      .carousel-card {
        @apply w-[47%] sm:w-[48%] md:w-4/12 min-h-[200px] sm:min-h-[285px] lg:min-h-[485px] overflow-hidden relative;
        &::before {
          @apply bg-white rounded-2xl content-[""] overflow-hidden absolute w-full h-full z-1 top-0 transition-transform duration-300 ease-in-out;
        }
        &:hover {
          .product-carousel-image {
            @apply transform scale-110 transition-transform duration-300 ease-in-out;
          }
          &:before {
            @apply transform scale-95 transition-transform duration-300 ease-in-out;
          }

          p {
            @apply transform translate-x-3 -translate-y-3 transition-transform duration-300 ease-in-out;
          }
        }
        .carousel-card-link {
          @apply z-2 flex flex-col items-center justify-between p-8 h-full;
          .product-carousel-image {
            @apply object-contain w-[525px] transition-transform duration-300 ease-in-out z-2 rounded-full;
          }
          p {
            @apply text-center self-start z-2 transition-transform duration-300 ease-in-out;
          }
        }
      }
    }
}
</style>
